import React, { useEffect, useState } from 'react';
import shortid from 'shortid';
import { PlusIcon, MinusIcon, ChevronDownIcon } from '@rsos/assets/icons';
import Loader from '@rsos/components/capstone/Loader';
import Box from '@rsos/components/capstone/base/Box';
import Button from '@rsos/components/capstone/base/Button';
import { DefaultRootBody, Renderer } from '@rsos/ellington';
import DisclaimerBanner from '../DisclaimerBanner/DisclaimerBanner';
import { RADEError, RADENoData } from '../RADEErrors';
import {
  StyledCard,
  StyledSubHeaderArea,
  StyledCardBody,
  StyledText,
  DataPanel,
  DataPanelContent,
  BackButton,
  MAFID,
} from './CustomCard.styles';
import CustomCardHeader from './CustomCardHeader';

const LoadingText = ({ mafid }) => {
  return (
    <StyledText>
      Loading Additional Information
      <br />
      {mafid}
    </StyledText>
  );
};

const CollapsibleButton = ({ isCollapsed, onClick }) => {
  return (
    <Button
      btnType="secondary"
      size="xsmall"
      style={{ width: 24, margin: 0, marginTop: 10, marginRight: 10 }}
      onClick={onClick}
    >
      {isCollapsed ? (
        <PlusIcon width={10} height={10} />
      ) : (
        <MinusIcon width={10} height={10} />
      )}
    </Button>
  );
};

const CustomCard = ({
  schema,
  children,
  showDisclaimer,
  source,
  isChildRoot,
  currentData,
  onCloseDataPanel,
  onFetchDataLink,
  cardBodyRef,
  dataPath,
  selectedCallerID,
  collapsible = false,
  summarized = false,
  createdTime = undefined,
  borderless = false,
  noHeader = false,
  onClick = () => {},
  isAviveRequest = false,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [fetchedData, setFetchedData] = useState(null);
  const [error, setError] = useState(null);
  const { title, description } = schema;
  const [isCollapsed, setIsCollapsed] = useState(collapsible || summarized);

  useEffect(() => {
    let isMounted = true;
    if (currentData) {
      (async () => {
        setIsFetching(true);
        setError(null);
        try {
          const cardData = await onFetchDataLink(currentData);
          if (isMounted) {
            setFetchedData(cardData);
          }
        } catch (action) {
          if (isMounted) {
            setError(action.error || action);
          }
        } finally {
          if (isMounted) {
            setIsFetching(false);
          }
        }
      })();
    }
    return () => {
      isMounted = false;
    };
  }, [currentData, onCloseDataPanel, onFetchDataLink]);

  const hideScroll =
    isFetching || (fetchedData && fetchedData.length === 0) || error;

  return (
    <StyledCard
      className={`CustomCard ${isChildRoot ? 'is-child-root' : ''}`}
      isOpen={isOpen}
      data-name="adr-card"
      borderless={borderless}
      onClick={onClick}
      summarized={summarized}
    >
      <Box horizontal justify="space-between">
        {!noHeader && (
          <CustomCardHeader
            title={title}
            description={description}
            isChildRoot={isChildRoot}
            createdTime={createdTime}
            onClick={() => setIsOpen(true)}
            data-name="adr-card-header"
            dataPath={dataPath}
            selectedCallerID={selectedCallerID}
          />
        )}

        {summarized && (
          <Button
            btnType="secondary"
            size="xsmall"
            style={{
              width: 24,
              margin: 0,
              marginTop: 16,
              marginRight: 20,
              rotate: '-90deg',
            }}
            onClick={() => {}}
          >
            <ChevronDownIcon width={10} height={10} />
          </Button>
        )}

        {collapsible && (
          <CollapsibleButton
            isCollapsed={isCollapsed}
            onClick={() => {
              setIsCollapsed(prev => !prev);
            }}
          />
        )}
      </Box>
      {isCollapsed ? null : (
        <>
          {isOpen && showDisclaimer && <DisclaimerBanner source={source} />}
          {isOpen && (fetchedData || error) && (
            <StyledSubHeaderArea>
              {currentData && <MAFID>Viewing {currentData.mafid}</MAFID>}
              <BackButton onClick={onCloseDataPanel}>&larr; Go Back</BackButton>
            </StyledSubHeaderArea>
          )}
          <StyledCardBody
            isAviveRequest={isAviveRequest}
            hideScroll={hideScroll}
            ref={cardBodyRef}
            data-name="adr-card-body"
          >
            {isOpen && (
              <>
                {(isFetching || fetchedData || error) && (
                  <DataPanel>
                    {isFetching ? (
                      <Box align="center" justify="center" mt={50}>
                        <Loader
                          fluid
                          text={<LoadingText mafid={currentData.mafid} />}
                        />
                      </Box>
                    ) : (
                      <DataPanelContent>
                        {error ? (
                          <Box
                            align="center"
                            justify="center"
                            style={{ marginTop: 60 }}
                          >
                            <RADEError />
                          </Box>
                        ) : fetchedData.length === 0 ? (
                          <Box
                            align="center"
                            justify="center"
                            style={{ marginTop: 60 }}
                          >
                            <RADENoData />
                          </Box>
                        ) : (
                          <>
                            {fetchedData.map(dataset => {
                              const { data, display_schema } = dataset;
                              return (
                                <Renderer
                                  key={shortid.generate()}
                                  data={data}
                                  displaySchema={display_schema}
                                  showExitButton={false}
                                  isChildRoot
                                  cardBodyRef={cardBodyRef}
                                  source={''}
                                />
                              );
                            })}
                          </>
                        )}
                      </DataPanelContent>
                    )}
                  </DataPanel>
                )}
                <DefaultRootBody children={children} />
              </>
            )}
          </StyledCardBody>
        </>
      )}
    </StyledCard>
  );
};

export default CustomCard;
